import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { ApiCustomer } from "src/api/api_customer";
import { useForm } from "@tanstack/react-form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import Form from "src/components/base/form";
import WS from "src/api/ws";
import TablePagination from "src/components/table-pagination";

/**
 * The properties for {@link LocalGroupOverview}
 */
export type LocalGroupOverviewProps = {};

const LIMIT = 25;

/**
 * An overview of all available groups
 */
function LocalGroupOverview(props: LocalGroupOverviewProps) {
    const [t] = useTranslation("customer-groups");

    const navigate = useNavigate();
    const router = useRouter();

    const { search, page } = Route.useSearch();
    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    const form = useForm({
        defaultValues: {
            search: search,
        },
        onSubmit: async ({ value }) => {
            await navigate({
                to: "/c/groups/local",
                search: {
                    search: value.search,
                    page: 1,
                },
            });
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.GroupsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) =>
                                    await navigate({
                                        to: "/c/groups/local",
                                        search: {
                                            search: fieldApi.state.value,
                                            page: 1,
                                        },
                                    }),
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>
            </Form>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.group-name")}</TableHeader>
                        <TableHeader>{t("label.internet-access")}</TableHeader>
                        <TableHeader>{t("label.description")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((group) => (
                        <TableRow key={group.uuid} href={"/c/groups/$groupId/general"} params={{ groupId: group.uuid }}>
                            <TableCell>{group.name}</TableCell>
                            <TableCell>
                                {group.internet ? <CheckCircleIcon color={"green"} className={"size-6"} /> : undefined}
                            </TableCell>
                            <TableCell>{group.description}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/c/groups/local"}
                getSearchParams={(newPage: number) => ({ search, page: newPage })}
            />
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type GroupsOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_customer/c/groups/_groups/local")({
    component: LocalGroupOverview,
    validateSearch: (search: Record<string, unknown>): GroupsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page, search: (search.search as string) || "" };
    },
    loaderDeps: ({ search: { search, page } }) => ({ search, page }),
    loader: async ({ deps }) =>
        await ApiCustomer.groups.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            provider: "Local",
        }),
});
